import React from 'react'

import { CheckBox, Input } from 'shared/components'

import styles from './Multiselect.module.scss'

interface MultiSelectProps<T> {
  options: Array<T>
  onSelect: (values: T[], value: T) => void
  onRemove: (values: T[], value: T) => void
  singleSelect?: boolean
  selectedValues?: Array<T>
  showCheckbox?: boolean
  selectionLimit?: number
  placeholder?: string
  displayValue: string
  emptyRecordMsg?: string
  closeOnSelect?: boolean
  id?: string
  hidePlaceholder?: boolean
  disable?: boolean
  onSearch?: (filter: string) => void
  loading?: boolean
  loadingMessage?: string
  input?: { value?: any; onChange?: (value: T | Array<T>) => void }
  valuePath?: string
  label?: string
  required?: boolean
  readonly?: boolean
  dark?: boolean
}

export class Multiselect<T> extends React.Component {
  searchWrapper: React.RefObject<HTMLDivElement>
  searchBox: React.RefObject<HTMLInputElement>

  static defaultProps = {
    options: [],
    selectedValues: [],
    displayValue: '',
    selectionLimit: -1,
    placeholder: '',
    emptyRecordMsg: 'По вашему запросу ничего не найдено',
    onSelect: (): boolean => false,
    onRemove: (): boolean => false,
    singleSelect: false,
    id: '',
    closeOnSelect: false,
    hidePlaceholder: false,
    label: '',
    readonly: false,
  }

  state: {
    inputValue: string
    options: Array<T>
    filteredOptions: Array<T>
    unfilteredOptions: Array<T>
    selectedValues: Array<T>
    preSelectedValues: Array<T>
    toggleOptionsList: boolean
    highlightOption: number
    index?: number
  }

  isSelect = false

  constructor(public props: MultiSelectProps<T>) {
    super(props)
    this.state = {
      inputValue: '',
      options: props.options,
      filteredOptions: props.options,
      unfilteredOptions: props.options,
      selectedValues: Object.assign([], props.selectedValues),
      preSelectedValues: Object.assign([], props.selectedValues),
      toggleOptionsList: false,
      highlightOption: 0,
    }

    this.searchWrapper = React.createRef()
    this.searchBox = React.createRef()

    this.onChange = this.onChange.bind(this)
    this.renderMultiselectContainer = this.renderMultiselectContainer.bind(this)

    this.onRemoveSelectedItem = this.onRemoveSelectedItem.bind(this)
    this.toggleOptionList = this.toggleOptionList.bind(this)
    this.onSelectItem = this.onSelectItem.bind(this)
    this.filterOptionsByInput = this.filterOptionsByInput.bind(this)

    this.isSelectedValue = this.isSelectedValue.bind(this)
    this.fadeOutSelection = this.fadeOutSelection.bind(this)
    this.renderNormalOption = this.renderNormalOption.bind(this)
    this.listenerCallback = this.listenerCallback.bind(this)
  }

  componentDidMount(): void {
    this.searchWrapper?.current?.addEventListener(
      'click',
      this.listenerCallback
    )
    if (
      this.props.input?.value !== '' &&
      typeof this.props.input?.value !== 'undefined'
    ) {
      this.setState({
        selectedValues: this.finSelectedValues(),
      })
    }
  }

  finSelectedValues(): Array<T> {
    const { singleSelect, input } = this.props
    const value = input?.value
    if (singleSelect) {
      return [this.findSingleValue(value)]
    }
    if (Array.isArray(value)) {
      return value.map((item: any) => this.findSingleValue(item))
    }
    return []
  }

  findSingleValue(searchValue: T): T {
    const { valuePath, options } = this.props

    const option = options.find(item => {
      const check = valuePath && item ? Object(item)[valuePath] : item
      return check === searchValue
    })

    return option as T
  }

  componentDidUpdate(prevProps: MultiSelectProps<T>): void {
    const { options, selectedValues, input } = this.props
    const { options: prevOptions, selectedValues: prevSelectedValues } =
      prevProps

    if (JSON.stringify(prevOptions) !== JSON.stringify(options)) {
      let findSelect: (T | undefined)[] | undefined | [] = selectedValues

      if (input?.value !== '') {
        findSelect = this.finSelectedValues()
      }

      this.setState({
        options,
        filteredOptions: options,
        unfilteredOptions: options,
        selectedValues: findSelect,
      })
      return
    }

    if (JSON.stringify(prevSelectedValues) !== JSON.stringify(selectedValues)) {
      this.setState({
        selectedValues: selectedValues,
      })
      return
    }

    if (input?.value !== prevProps?.input?.value) {
      this.setState({
        selectedValues: this.finSelectedValues(),
      })
    }
  }

  listenerCallback(e: MouseEvent): void {
    const target = e.target as HTMLElement
    if (target.tagName === 'I') {
      return
    }
    this.searchBox?.current?.focus()
  }

  componentWillUnmount(): void {
    this.searchWrapper?.current?.removeEventListener(
      'click',
      this.listenerCallback
    )
  }

  onChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const { onSearch } = this.props
    this.setState({ inputValue: event.target.value }, this.filterOptionsByInput)
    if (onSearch) {
      onSearch(event.target.value)
    }
  }

  filterOptionsByInput(): void {
    const { filteredOptions, inputValue } = this.state
    const { displayValue } = this.props

    const options = filteredOptions.filter(option =>
      this.matchValues(
        displayValue ? Object(option)[displayValue] : option,
        inputValue
      )
    )

    this.setState({ options })
  }

  matchValues(value: string, search: string): boolean {
    return value.toLowerCase().indexOf(search.toLowerCase()) > -1
  }

  onRemoveSelectedItem(item: T): void {
    const { selectedValues } = this.state
    const { onRemove, valuePath } = this.props
    let itemIndex
    if (valuePath) {
      itemIndex = selectedValues.findIndex(
        selectedValue =>
          Object(selectedValue)[valuePath] === Object(item)[valuePath]
      )
    } else {
      itemIndex = selectedValues.findIndex(
        selectedValue => selectedValue === item
      )
    }

    selectedValues.splice(itemIndex, 1)

    onRemove(selectedValues, item)

    this.setState({ selectedValues })

    this.formChange(selectedValues)

    if (!this.props.closeOnSelect) {
      this.searchBox?.current?.focus()
    }
  }

  getOnChangeValues(values: Array<T>): Array<T> {
    const { valuePath } = this.props

    if (valuePath) {
      return values.map(item => Object(item)[valuePath])
    }
    return values
  }

  formChange(values: Array<T>): void {
    const { input, singleSelect } = this.props
    const { toggleOptionsList } = this.state

    if (input?.onChange) {
      const selectedValues = this.getOnChangeValues(values)

      input.onChange(singleSelect ? selectedValues[0] : selectedValues)
    }
    if (singleSelect && toggleOptionsList) {
      this.searchBox?.current?.blur()
    }
  }

  onSelectItem(
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    item: T
  ): void {
    event.preventDefault()
    event.stopPropagation()
    this.isSelect = true
    const { selectedValues } = this.state
    const { selectionLimit, onSelect, singleSelect } = this.props

    if (singleSelect) {
      this.setState({
        inputValue: '',
      })
      this.onSingleSelect(item)
      onSelect([item], item)
      this.formChange([item])
      return
    }

    if (this.isSelectedValue(item)) {
      this.onRemoveSelectedItem(item)
      return
    }

    if (selectionLimit === selectedValues.length) {
      return
    }
    selectedValues.push(item)
    onSelect(selectedValues, item)
    this.isSelect = false
    this.setState({ selectedValues }, () => {
      this.filterOptionsByInput()
    })
    this.formChange(selectedValues)
  }

  onSingleSelect(item: T): void {
    this.setState({
      selectedValues: [item],
      toggleOptionsList: false,
    })
  }

  isSelectedValue(item: T): boolean {
    const { valuePath } = this.props
    const { selectedValues } = this.state
    if (valuePath) {
      const itemToObj = Object(item)
      return (
        selectedValues.filter(
          selectedItem =>
            Object(selectedItem)[valuePath] === itemToObj[valuePath]
        ).length > 0
      )
    }
    return selectedValues.filter(i => i === item).length > 0
  }

  renderOptionList(): JSX.Element {
    const {
      emptyRecordMsg,
      loading,
      loadingMessage = 'loading...',
    } = this.props
    const { options } = this.state
    if (loading) {
      return (
        <ul className={`optionContainer`}>
          {typeof loadingMessage === 'string' && (
            <span className={`notFound ${styles.notFound}`}>
              {loadingMessage}
            </span>
          )}
          {typeof loadingMessage !== 'string' && loadingMessage}
        </ul>
      )
    }
    return (
      <ul className={`optionContainer`}>
        {options.length === 0 && (
          <span className={`notFound ${styles.notFound}`}>
            {emptyRecordMsg}
          </span>
        )}
        {this.renderNormalOption()}
      </ul>
    )
  }

  renderNormalOption(): JSX.Element {
    const { displayValue, singleSelect } = this.props

    return (
      <>
        {this.state.options.map((option, optionIndex) => {
          const isSelected = this.isSelectedValue(option)
          return (
            <li
              key={`option${optionIndex}`}
              className={`${
                isSelected ? `${styles.highlightOption} highlight` : ''
              } ${
                (this.fadeOutSelection(option) || Object(option)?.disable) &&
                styles.disable
              }  ${
                singleSelect && isSelected ? styles.singleHighlight : ''
              } option`}
              onMouseDown={event => this.onSelectItem(event, option)}
            >
              {!singleSelect && (
                <CheckBox
                  dark={this.props?.dark}
                  small
                  readOnly
                  checked={isSelected}
                  title={' '}
                ></CheckBox>
              )}
              {displayValue
                ? Object(option)[displayValue]
                : (`${option}` || '').toString()}
            </li>
          )
        })}
      </>
    )
  }

  fadeOutSelection(item: T): boolean {
    const { selectionLimit, singleSelect } = this.props
    if (singleSelect) {
      return false
    }
    const { selectedValues } = this.state
    if (selectionLimit === -1) {
      return false
    }
    if (selectionLimit !== selectedValues.length) {
      return false
    }
    if (selectionLimit === selectedValues.length) {
      if (this.isSelectedValue(item)) {
        return false
      }
      return true
    }
    return false
  }

  toggleOptionList(): void {
    this.setState(
      {
        inputValue: '',
        toggleOptionsList: !this.state.toggleOptionsList,
        highlightOption: 0,
      },
      () => {
        this.filterOptionsByInput()
      }
    )
  }

  getPostfixClassName(): string {
    const { toggleOptionsList, inputValue } = this.state
    if (!toggleOptionsList) {
      return `${styles.icon} ${styles.icon_up}`
    }
    if (inputValue) {
      return `${styles.icon} ${styles.icon_clear}`
    }
    return `${styles.icon} ${styles.icon_down}`
  }

  onPostfixClick(event: React.MouseEvent<HTMLElement, MouseEvent>): void {
    event.preventDefault()
    event.stopPropagation()
    if (this.state.inputValue) {
      return this.setState({ inputValue: '' }, () => {
        this.filterOptionsByInput()
      })
    }
    if (this.state.toggleOptionsList) {
      return this.searchBox.current?.blur()
    }
    this.searchBox.current?.focus()
  }

  handlerBlur(): void {
    return this.toggleOptionList()
  }

  handleInputClick(): void {
    if (!this.state.toggleOptionsList) {
      this.toggleOptionList()
    }
  }

  renderSelectedList(): JSX.Element {
    const { singleSelect, displayValue } = this.props

    const { selectedValues, toggleOptionsList } = this.state
    if (!singleSelect || toggleOptionsList) {
      return <></>
    }
    return (
      <span
        className={`chip ${styles.chip} ${singleSelect && styles.singleChip}`}
      >
        {displayValue
          ? Object(selectedValues[0])[displayValue]
          : selectedValues[0]}
      </span>
    )
  }

  renderMultiselectContainer(): JSX.Element {
    const { inputValue, toggleOptionsList, selectedValues } = this.state
    const {
      placeholder,
      singleSelect,
      id,
      hidePlaceholder,
      disable,
      required,
      dark,
    } = this.props
    const highlight = !hidePlaceholder && selectedValues.length && !singleSelect
    const classes = `${styles.input} ${
      toggleOptionsList ? styles.opened : ''
    } ${highlight ? styles.inputHighlight : ''}`

    return (
      <div
        className={`${styles.multiSelectContainer} ${
          disable ? `${styles.disable}` : ''
        } ${dark ? styles.dark : ''}`}
        id={id || 'multiselectContainerId'}
      >
        <div ref={this.searchWrapper}>
          <Input
            type="text"
            onChange={this.onChange}
            value={inputValue}
            className={classes}
            onFocus={this.toggleOptionList}
            onBlur={this.toggleOptionList}
            required={required}
            childRef={this.searchBox}
            children={this.renderSelectedList()}
            dark={this.props?.dark}
            label={this.props?.label}
            readOnly={this.props.readonly}
            placeholder={
              (singleSelect && selectedValues.length) ||
              (hidePlaceholder && selectedValues.length)
                ? ''
                : placeholder
            }
            disabled={disable}
            autoComplete="none" /* при значении "off" автокомплит всё равно срабатывает, значение "none" фиксит эту проблему */
            postfix={
              <i
                className={this.getPostfixClassName()}
                onMouseDown={e => this.onPostfixClick(e)}
              />
            }
          />
          {singleSelect && <i className={`icon_cancel ${styles.icon_down}`} />}
        </div>
        <div
          className={`optionListContainer  ${styles.optionListContainer} ${
            toggleOptionsList ? styles.displayBlock : styles.displayNone
          }`}
        >
          {this.renderOptionList()}
        </div>
      </div>
    )
  }

  render(): JSX.Element {
    return this.renderMultiselectContainer()
  }
}
